import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';

const UploadModal = ({ language, isOpen, onClose, onUpload }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadFolder, setUploadFolder] = useState('');
  const fileInputRef = useRef(null);

  const folderOptions = [
    { value: 'client-contracts', label: language === 'fr' ? 'Contrats clients' : language === 'ar' ? 'عقود العملاء' : 'Client Contracts' },
    { value: 'case-pleadings', label: language === 'fr' ? 'Plaidoiries' : language === 'ar' ? 'المرافعات' : 'Case Pleadings' },
    { value: 'case-evidence', label: language === 'fr' ? 'Preuves' : language === 'ar' ? 'الأدلة' : 'Evidence' },
    { value: 'client-identification', label: language === 'fr' ? 'Documents d\'identité' : language === 'ar' ? 'وثائق الهوية' : 'Identification' },
    { value: 'invoices', label: language === 'fr' ? 'Factures' : language === 'ar' ? 'الفواتير' : 'Invoices' }
  ];

  const titleLabel = {
    en: 'Upload Documents',
    fr: 'Télécharger des documents',
    ar: 'تحميل المستندات'
  };

  const dragDropLabel = {
    en: 'Drag and drop files here, or click to select',
    fr: 'Glissez-déposez les fichiers ici ou cliquez pour sélectionner',
    ar: 'اسحب وأفلت الملفات هنا، أو انقر للتحديد'
  };

  const folderLabel = {
    en: 'Upload to folder',
    fr: 'Télécharger dans le dossier',
    ar: 'التحميل إلى المجلد'
  };

  const supportedLabel = {
    en: 'Supported formats: PDF, DOCX, JPG, PNG (Max 10MB)',
    fr: 'Formats supportés: PDF, DOCX, JPG, PNG (Max 10Mo)',
    ar: 'التنسيقات المدعومة: PDF، DOCX، JPG، PNG (الحد الأقصى 10 ميجابايت)'
  };

  const uploadButtonLabel = {
    en: 'Upload Files',
    fr: 'Télécharger les fichiers',
    ar: 'تحميل الملفات'
  };

  const cancelLabel = {
    en: 'Cancel',
    fr: 'Annuler',
    ar: 'إلغاء'
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleChange = (e) => {
    e?.preventDefault();
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFiles(e?.target?.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files)?.map(file => ({
      file,
      name: file?.name,
      size: (file?.size / 1024 / 1024)?.toFixed(2) + ' MB',
      type: file?.name?.split('.')?.pop()?.toUpperCase()
    }));
    setSelectedFiles(prev => [...prev, ...fileArray]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (selectedFiles?.length > 0 && uploadFolder) {
      onUpload({ files: selectedFiles, folder: uploadFolder });
      setSelectedFiles([]);
      setUploadFolder('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {titleLabel?.[language]}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-border'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef?.current?.click()}
          >
            <Icon name="Upload" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-foreground mb-2">
              {dragDropLabel?.[language]}
            </p>
            <p className="text-xs text-muted-foreground">
              {supportedLabel?.[language]}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleChange}
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
          </div>

          {selectedFiles?.length > 0 && (
            <div className="space-y-2">
              {selectedFiles?.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-muted rounded-lg"
                >
                  <Icon name="FileText" size={20} className="text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {file?.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {file?.type} • {file?.size}
                    </p>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 hover:bg-background rounded transition-colors flex-shrink-0"
                    aria-label="Remove file"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Select
            label={folderLabel?.[language]}
            options={folderOptions}
            value={uploadFolder}
            onChange={setUploadFolder}
            required
          />
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            {cancelLabel?.[language]}
          </Button>
          <Button
            variant="default"
            onClick={handleUpload}
            disabled={selectedFiles?.length === 0 || !uploadFolder}
            iconName="Upload"
            iconPosition="left"
          >
            {uploadButtonLabel?.[language]}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;